// src/pages/SavedAnalysisView.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaCircleArrowLeft } from "react-icons/fa6";
import { ArrowRight, BarChart, TrendingUp, DollarSign, Users, AlertTriangle, Rocket, Zap, Map, Target, Shield, Lightbulb, Target as TargetIcon, FileText, Calendar } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { useAuthCheck } from '../hooks/useAuthCheck';

function SavedAnalysisView() {
    useAuthCheck();

    const { id } = useParams();
    const navigate = useNavigate();
    const [analysis, setAnalysis] = useState(null);
    const [analysisData, setAnalysisData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [downloadingFull, setDownloadingFull] = useState(false);

    // Existing Business Analysis Sections
    const existingSections = [
        { title: "Current Performance", icon: <BarChart size={20} />, key: "currentPerformance", color: "blue", type: 'existing' },
        { title: "SWOT Analysis", icon: <Target size={20} />, key: "swotAnalysis", color: "green", type: 'existing' },
        { title: "Growth Opportunities", icon: <TrendingUp size={20} />, key: "growthOpportunities", color: "yellow", type: 'existing' },
        { title: "Risk Assessment", icon: <AlertTriangle size={20} />, key: "riskAssessment", color: "red", type: 'existing' },
        { title: "Operational Efficiency", icon: <Zap size={20} />, key: "operationalEfficiency", color: "emerald", type: 'existing' },
        { title: "Financial Health", icon: <DollarSign size={20} />, key: "financialHealth", color: "purple", type: 'existing' },
        { title: "Marketing Strategy", icon: <TargetIcon size={20} />, key: "marketingStrategy", color: "indigo", type: 'existing' },
        { title: "Competitive Analysis", icon: <Shield size={20} />, key: "competitiveAnalysis", color: "orange", type: 'existing' },
        { title: "Customer Analysis", icon: <Users size={20} />, key: "customerAnalysis", color: "teal", type: 'existing' },
        { title: "Technology Assessment", icon: <Zap size={20} />, key: "technologyAssessment", color: "cyan", type: 'existing' },
        { title: "Team Optimization", icon: <Users size={20} />, key: "teamOptimization", color: "pink", type: 'existing' },
        { title: "Expansion Strategy", icon: <Map size={20} />, key: "expansionStrategy", color: "gray", type: 'existing' },
    ];

    // New Business Analysis Sections
    const newSections = [
        { title: "Deep Market Analysis", icon: <TrendingUp size={20} />, key: "marketAnalysis", color: "blue", type: 'new' },
        { title: "Target Audience Research", icon: <Users size={20} />, key: "audienceResearch", color: "green", type: 'new' },
        { title: "Required Resources", icon: <Zap size={20} />, key: "requiredResources", color: "yellow", type: 'new' },
        { title: "Cost Estimation", icon: <DollarSign size={20} />, key: "costEstimation", color: "emerald", type: 'new' },
        { title: "Competitor Breakdown", icon: <Target size={20} />, key: "competitorBreakdown", color: "red", type: 'new' },
        { title: "Execution Strategy", icon: <Rocket size={20} />, key: "executionStrategy", color: "purple", type: 'new' },
        { title: "Unique Selling Points", icon: <Lightbulb size={20} />, key: "uniqueSellingPoints", color: "indigo", type: 'new' },
        { title: "Risk Analysis", icon: <AlertTriangle size={20} />, key: "riskAnalysis", color: "orange", type: 'new' },
        { title: "Monetization Plan", icon: <DollarSign size={20} />, key: "monetizationPlan", color: "teal", type: 'new' },
        { title: "Full Roadmap", icon: <Map size={20} />, key: "roadmap", color: "pink", type: 'new' },
        { title: "Business Growth Strategy", icon: <TrendingUp size={20} />, key: "growthStrategy", color: "cyan", type: 'new' },
        { title: "Chat About Your Idea", icon: <Users size={20} />, key: "chatSupport", color: "gray", type: 'new' },
    ];

    useEffect(() => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth'
        });
    }, []);

    useEffect(() => {
        fetchAnalysis();
    }, [id]);

    const fetchAnalysis = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }

        try {
            const response = await fetch(`https://insightdraft-backend.onrender.com/api/analyses/saved/${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();

            if (data.success) {
                const savedAnalysis = data.analysis;

                // Parse data
                let parsedData = savedAnalysis.data;
                if (typeof parsedData === 'string') {
                    parsedData = JSON.parse(parsedData);
                }

                const dataKeys = Object.keys(parsedData || {});
                const isSingleSection = dataKeys.length === 1;
                const analysisType = savedAnalysis.type?.includes('existing') ? 'existing' : 'new';

                // ✅ If single section, redirect to category page
                if (isSingleSection) {
                    const sectionKey = dataKeys[0];

                    // Save to localStorage
                    localStorage.setItem('analysisType', analysisType);

                    if (analysisType === 'existing') {
                        localStorage.setItem('businessAnalysis', JSON.stringify(parsedData));
                        localStorage.setItem('businessFormData', JSON.stringify({ businessName: savedAnalysis.title }));
                        navigate(`/saved/${id}/category/${sectionKey}`, { replace: true });
                    } else {
                        localStorage.setItem('newBusinessAnalysis', JSON.stringify(parsedData));
                        localStorage.setItem('newBusinessFormData', JSON.stringify({ idea: savedAnalysis.title }));
                        navigate(`/saved/${id}/category-new/${sectionKey}`, { replace: true });
                    }
                    return;
                }

                // Full analysis - show overview
                setAnalysis(savedAnalysis);
                setAnalysisData(parsedData);
                localStorage.setItem('analysisType', analysisType);
            } else {
                setError(data.error || 'Analysis not found');
            }
        } catch (err) {
            console.error('Fetch error:', err);
            setError('Failed to load analysis');
        } finally {
            setLoading(false);
        }
    };

    const getColorClasses = (color) => {
        const colors = {
            blue: { border: 'border-blue-200', bg: 'bg-blue-50', hover: 'hover:bg-blue-100', text: 'text-blue-700', button: 'btn-outline-primary', cardbg: '#dbeafe', cardborder: '#93c5fd', cardtext: '#1e40af' },
            green: { border: 'border-green-200', bg: 'bg-green-50', hover: 'hover:bg-green-100', text: 'text-green-700', button: 'btn-outline-success', cardbg: '#dcfce7', cardborder: '#86efac', cardtext: '#166534' },
            yellow: { border: 'border-yellow-200', bg: 'bg-yellow-50', hover: 'hover:bg-yellow-100', text: 'text-yellow-700', button: 'btn-outline-warning', cardbg: '#fef9c3', cardborder: '#fde047', cardtext: '#854d0e' },
            emerald: { border: 'border-emerald-200', bg: 'bg-emerald-50', hover: 'hover:bg-emerald-100', text: 'text-emerald-700', button: 'btn-outline-success', cardbg: '#d1fae5', cardborder: '#6ee7b7', cardtext: '#065f46' },
            red: { border: 'border-red-200', bg: 'bg-red-50', hover: 'hover:bg-red-100', text: 'text-red-700', button: 'btn-outline-danger', cardbg: '#fee2e2', cardborder: '#fca5a5', cardtext: '#991b1b' },
            purple: { border: 'border-purple-200', bg: 'bg-purple-50', hover: 'hover:bg-purple-100', text: 'text-purple-700', button: 'btn-outline-primary', cardbg: '#f3e8ff', cardborder: '#c4b5fd', cardtext: '#6b21a8' },
            indigo: { border: 'border-indigo-200', bg: 'bg-indigo-50', hover: 'hover:bg-indigo-100', text: 'text-indigo-700', button: 'btn-outline-primary', cardbg: '#e0e7ff', cardborder: '#a5b4fc', cardtext: '#3730a3' },
            orange: { border: 'border-orange-200', bg: 'bg-orange-50', hover: 'hover:bg-orange-100', text: 'text-orange-700', button: 'btn-outline-warning', cardbg: '#ffedd5', cardborder: '#fdba74', cardtext: '#9a3412' },
            teal: { border: 'border-teal-200', bg: 'bg-teal-50', hover: 'hover:bg-teal-100', text: 'text-teal-700', button: 'btn-outline-info', cardbg: '#ccfbf1', cardborder: '#5eead4', cardtext: '#134e4a' },
            pink: { border: 'border-pink-200', bg: 'bg-pink-50', hover: 'hover:bg-pink-100', text: 'text-pink-700', button: 'btn-outline-danger', cardbg: '#fce7f3', cardborder: '#f9a8d4', cardtext: '#831843' },
            cyan: { border: 'border-cyan-200', bg: 'bg-cyan-50', hover: 'hover:bg-cyan-100', text: 'text-cyan-700', button: 'btn-outline-info', cardbg: '#cffafe', cardborder: '#5eead4', cardtext: '#164e63' },
            gray: { border: 'border-gray-200', bg: 'bg-gray-50', hover: 'hover:bg-gray-100', text: 'text-gray-700', button: 'btn-outline-secondary', cardbg: '#f3f4f6', cardborder: '#d1d5db', cardtext: '#374151' },
        };
        return colors[color] || colors.gray;
    };

    // Format text for PDF
    const formatTextForPDF = (text) => {
        if (!text) return '';
        let processedText = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        processedText = processedText.replace(/\*(.*?)\*/g, '<em>$1</em>');
        const lines = processedText.split('\n');
        let html = '';
        let i = 0;

        while (i < lines.length) {
            let trimmed = lines[i].trim();
            const nextTrimmed = (lines[i + 1] || '').trim();

            if (trimmed.includes('|') && (nextTrimmed.includes('---') || nextTrimmed.includes('==='))) {
                const tableLines = [];
                while (i < lines.length && lines[i].trim().includes('|')) {
                    tableLines.push(lines[i].trim());
                    i++;
                }
                html += buildTableHTML(tableLines);
                continue;
            }

            if (!trimmed) { html += '<div style="height:6px"></div>'; i++; continue; }

            if (trimmed.endsWith(':') && trimmed.length < 100 && !trimmed.match(/^[•*\-+\d]/)) {
                html += `<div style="font-weight:700;font-size:14px;color:#1e3a8a;margin:16px 0 6px 0;">${trimmed}</div>`;
                i++; continue;
            }

            const words = trimmed.split(' ');
            if (trimmed === trimmed.toUpperCase() && words.length >= 2 && words.length <= 8 && trimmed.length < 80 && !trimmed.match(/^[•*\-+\d]/)) {
                html += `<div style="font-weight:700;font-size:14px;color:#1e3a8a;margin:16px 0 6px 0;">${trimmed}</div>`;
                i++; continue;
            }

            const numMatch = trimmed.match(/^(\d+\.)\s+(.+)/);
            if (numMatch) {
                html += `<div style="display:flex;gap:8px;margin:8px 0;font-size:13px;"><span style="font-weight:700;color:#1e3a8a;min-width:20px;">${numMatch[1]}</span><span>${numMatch[2]}</span></div>`;
                i++; continue;
            }

            const bulletMatch = trimmed.match(/^[•*\-+]\s+(.+)/);
            if (bulletMatch) {
                let bc = bulletMatch[1];
                const ci = bc.indexOf(':');
                if (ci > 0 && ci < bc.length - 1) {
                    html += `<div style="display:flex;gap:8px;margin:5px 0 5px 12px;font-size:13px;"><span style="color:#1e3a8a;font-weight:700;">•</span><span><strong>${bc.substring(0, ci + 1)}</strong> ${bc.substring(ci + 1).trim()}</span></div>`;
                } else {
                    html += `<div style="display:flex;gap:8px;margin:5px 0 5px 12px;font-size:13px;"><span style="color:#1e3a8a;font-weight:700;">•</span><span>${bc}</span></div>`;
                }
                i++; continue;
            }

            const ci = trimmed.indexOf(':');
            if (ci > 0 && ci < trimmed.length - 1 && !trimmed.substring(0, ci).includes('<strong>')) {
                html += `<div style="margin:8px 0;font-size:13px;"><strong style="color:#1e3a8a;">${trimmed.substring(0, ci + 1)}</strong><span style="margin-left:6px;">${trimmed.substring(ci + 1).trim()}</span></div>`;
                i++; continue;
            }

            html += `<p style="margin:6px 0;font-size:13px;line-height:1.7;">${trimmed}</p>`;
            i++;
        }
        return html;
    };

    const buildTableHTML = (rows) => {
        if (!rows || rows.length === 0) return '';
        let t = '<table style="width:100%;border-collapse:collapse;margin:12px 0;font-size:12px;">';
        rows.forEach((row, ri) => {
            if (row.replace(/[\|\-\s=]/g, '') === '') return;
            const cells = row.split('|').map(c => c.trim()).filter(Boolean);
            if (!cells.length) return;
            const isHead = ri === 0;
            t += `<tr style="background:${isHead ? '#dbeafe' : ri % 2 === 0 ? '#fff' : '#f9fafb'}">`;
            cells.forEach(cell => {
                const tag = isHead ? 'th' : 'td';
                const s = isHead ? 'padding:9px 12px;text-align:left;font-weight:700;color:#1e3a8a;border:1px solid #93c5fd;' : 'padding:8px 12px;text-align:left;border:1px solid #e5e7eb;vertical-align:top;';
                t += `<${tag} style="${s}">${cell}</${tag}>`;
            });
            t += '</tr>';
        });
        t += '</table>';
        return t;
    };

    const downloadFullReport = async () => {
        if (!analysisData) return;
        setDownloadingFull(true);

        try {
            const analysisType = analysis?.type?.includes('existing') ? 'existing' : 'new';
            const currentSections = analysisType === 'existing' ? existingSections : newSections;
            const businessName = analysis?.title || 'Business Analysis';

            const sectionBgColors = {
                currentPerformance: '#1e40af', swotAnalysis: '#166534',
                growthOpportunities: '#854d0e', riskAssessment: '#991b1b',
                operationalEfficiency: '#065f46', financialHealth: '#6b21a8',
                marketingStrategy: '#3730a3', competitiveAnalysis: '#9a3412',
                customerAnalysis: '#134e4a', technologyAssessment: '#164e63',
                teamOptimization: '#831843', expansionStrategy: '#374151',
                marketAnalysis: '#1e40af', audienceResearch: '#166534',
                requiredResources: '#854d0e', costEstimation: '#065f46',
                competitorBreakdown: '#991b1b', executionStrategy: '#6b21a8',
                uniqueSellingPoints: '#3730a3', riskAnalysis: '#9a3412',
                monetizationPlan: '#134e4a', roadmap: '#831843',
                growthStrategy: '#164e63', chatSupport: '#374151',
            };

            let sectionsHTML = '';

            currentSections.forEach((section, idx) => {
                const content = analysisData[section.key];
                if (!content) return;
                const bg = sectionBgColors[section.key] || '#1e40af';
                const contentHTML = formatTextForPDF(content);
                const pb = idx > 0 ? 'page-break-before:always;' : '';

                sectionsHTML += `
                <div style="${pb}padding-bottom:30px;">
                    <div style="background-color:${bg};padding:14px 20px;border-radius:6px 6px 0 0;">
                        <div style="font-size:10px;letter-spacing:1.5px;text-transform:uppercase;color:rgba(255,255,255,0.75);margin-bottom:4px;">
                            Section ${idx + 1} / ${currentSections.length}
                        </div>
                        <div style="font-size:18px;font-weight:700;color:#ffffff;">${section.title}</div>
                    </div>
                    <div style="padding:20px;background-color:#ffffff;border:1px solid #e5e7eb;border-top:none;border-radius:0 0 6px 6px;color:#374151;">
                        ${contentHTML}
                    </div>
                </div>`;
            });

            const fullHTML = `<!DOCTYPE html>
<html>
<head><meta charset="UTF-8">
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, Helvetica, sans-serif; color: #1f2937; background: #fff; }
</style>
</head>
<body>
<div style="padding:32px;max-width:800px;margin:0 auto;">
  <div style="background-color:#1e3a8a;color:#fff;border-radius:8px;padding:48px 36px;text-align:center;margin-bottom:36px;">
    <div style="font-size:11px;letter-spacing:3px;text-transform:uppercase;color:#93c5fd;margin-bottom:12px;">InsightDraft AI</div>
    <div style="font-size:26px;font-weight:800;color:#fff;line-height:1.3;margin-bottom:12px;">Saved Analysis Report</div>
    <div style="font-size:15px;color:#bfdbfe;font-style:italic;margin-bottom:20px;">"${businessName}"</div>
    <div style="height:1px;background-color:#3b82f6;max-width:240px;margin:0 auto 18px auto;"></div>
    <div style="font-size:12px;color:#93c5fd;line-height:2.0;">
      <span>Type: <strong style="color:#fff;">${analysisType === 'existing' ? 'Existing Business' : 'New Idea'}</strong></span>
      <br/>
      <span>Saved: <strong style="color:#fff;">${new Date(analysis?.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</strong></span>
    </div>
  </div>
  ${sectionsHTML}
  <div style="margin-top:40px;padding-top:16px;border-top:1px solid #e5e7eb;text-align:center;font-size:11px;color:#9ca3af;">
    <div style="font-weight:600;color:#6b7280;margin-bottom:3px;">InsightDraft AI</div>
    <div>This report is AI-generated and for informational purposes only. Confidential &bull; ${new Date().getFullYear()}</div>
  </div>
</div>
</body>
</html>`;

            const nameSlug = businessName.replace(/[^a-z0-9]/gi, '_').toLowerCase().substring(0, 35);

            await html2pdf()
                .set({
                    margin: [8, 8, 8, 8],
                    filename: `saved_report_${nameSlug}.pdf`,
                    image: { type: 'jpeg', quality: 0.97 },
                    html2canvas: { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff', windowWidth: 900 },
                    jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait', compress: true },
                    pagebreak: { mode: ['avoid-all', 'css'] },
                })
                .from(fullHTML, 'string')
                .save();

        } catch (err) {
            console.error('PDF error:', err);
            alert('PDF generation failed. Please try again.');
        } finally {
            setDownloadingFull(false);
        }
    };

    const SectionCard = ({ section }) => {
        const colorClass = getColorClasses(section.color);
        const content = analysisData ? analysisData[section.key] : null;

        const handleCardClick = () => {
            const analysisType = analysis?.type?.includes('existing') ? 'existing' : 'new';
            if (analysisType === 'existing') {
                navigate(`/saved/${id}/category/${section.key}`);
            } else {
                navigate(`/saved/${id}/category-new/${section.key}`);
            }
        };

        const formatPreviewText = (text) => {
            if (!text || text === 'Analysis not available.') return 'Analysis in progress...';
            return text.replace(/#/g, '').replace(/##/g, '').replace(/###/g, '').replace(/\*/g, '').replace(/\*\*(.*?)\*\*/g, '$1').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
        };

        const getPreview = () => {
            if (!content || content === 'Analysis not available.') return 'Analysis in progress...';
            let preview = formatPreviewText(content);
            const previewLength = window.innerWidth < 768 ? 80 : 120;
            preview = preview.substring(0, previewLength);
            if (content.length > previewLength) preview += '...';
            return preview;
        };

        const preview = getPreview();

        return (
            <div
                className={`card h-100 border-2 ${colorClass.cardborder} transition-all cursor-pointer`}
                style={{ borderRadius: '16px', transition: 'all 0.3s ease', cursor: 'pointer', minHeight: '180px', maxHeight: '250px' }}
                onClick={handleCardClick}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.15)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
            >
                <div className="card-body d-flex flex-column p-2 p-sm-3 p-md-4" style={{ height: '100%' }}>
                    <div className="d-flex align-items-center mb-2 mb-sm-3">
                        <div className={`rounded-circle p-1 p-sm-2 ${colorClass.bg} ${colorClass.text} me-2 me-sm-3 flex-shrink-0`}
                            style={{ width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {React.cloneElement(section.icon, { size: window.innerWidth < 768 ? 16 : 18 })}
                        </div>
                        <h6 className={`fw-bold mb-0 ${colorClass.text}`}
                            style={{ fontSize: 'clamp(0.875rem, 2vw, 1rem)', lineHeight: 1.3, wordBreak: 'break-word' }}>
                            {section.title}
                        </h6>
                    </div>
                    <div className="text-muted mb-2 mb-sm-3 flex-grow-1"
                        style={{ lineHeight: '1.5', fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                        {preview}
                    </div>
                    <div className="d-flex justify-content-between align-items-center mt-auto">
                        <small className={`${colorClass.text} fw-semibold`} style={{ fontSize: '0.75rem' }}>Click to view</small>
                        <ArrowRight size={window.innerWidth < 768 ? 14 : 16} className={colorClass.text} />
                    </div>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="min-vh-100 d-flex align-items-center justify-content-center"
                style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0e7ff 100%)' }}>
                <div className="text-center">
                    <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }} />
                    <h5 className="text-primary" style={{ fontSize: '20px' }}>Loading Saved Analysis...</h5>
                </div>
            </div>
        );
    }

    if (error || !analysis || !analysisData) {
        return (
            <div className="min-vh-100" style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0e7ff 100%)' }}>
                <div className="container py-5">
                    <div className="text-center">
                        <h1 className="text-primary">Analysis Not Found</h1>
                        <p className="text-muted">{error || 'This analysis may have been deleted or does not exist.'}</p>
                        <button className="btn btn-primary mt-3" onClick={() => navigate('/my-analyses')}>
                            Back to My Analyses
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const analysisType = analysis.type?.includes('existing') ? 'existing' : 'new';
    const currentSections = analysisType === 'existing' ? existingSections : newSections;
    const businessName = analysis.title || 'Business Analysis';
    const pageTitle = analysisType === 'existing' ? 'Saved Business Analysis' : 'Saved Idea Analysis';

    return (
        <div className="min-vh-100" style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0e7ff 100%)' }}>
            <div className="container py-4 py-md-5 px-3 px-md-4" style={{ maxWidth: 1200 }}>
                <div className="container-fluid px-0 mb-4 mb-md-5">
                    <div className="d-flex flex-column">
                        <div className="d-flex justify-content-between align-items-center w-100 mb-2 mb-md-3">
                            <button
                                className="btn btn-outline-primary d-flex align-items-center justify-content-center"
                                onClick={() => {
                                    navigate('/my-analyses');
                                }}
                                style={{
                                    borderRadius: '12px',
                                    padding: 'clamp(8px,2vw,10px) clamp(12px,2.5vw,20px)',
                                    fontSize: 'clamp(14px,3vw,16px)',
                                    height: 'clamp(40px,8vw,44px)',
                                    minWidth: 'clamp(40px,10vw,120px)',
                                    flexShrink: 0,
                                    borderWidth: '2px',
                                    transition: 'all 0.3s ease',
                                    alignSelf: 'flex-start',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#0d53bd';
                                    e.currentTarget.style.color = 'white';
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = '#0d53bd';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                }}
                            >
                                <span className="d-none d-xl-inline-flex align-items-center">
                                    <FaCircleArrowLeft className="me-2" size={18} />
                                    Back to Saved
                                </span>
                                <span className="d-none d-lg-inline-flex d-xl-none align-items-center">
                                    <FaCircleArrowLeft className="me-1" size={16} />
                                    Saved
                                </span>
                                <span className="d-none d-md-inline-flex d-lg-none align-items-center">
                                    <FaCircleArrowLeft size={16} />
                                    <span className="ms-1">Back</span>
                                </span>
                                <span className="d-inline-flex d-md-none align-items-center justify-content-center" title="Back to Saved">
                                    <FaCircleArrowLeft size={16} />
                                </span>
                            </button>

                            {/* ✅ Responsive Download Button */}
                            <button
                                onClick={downloadFullReport}
                                disabled={downloadingFull}
                                className="btn btn-outline-primary d-flex align-items-center justify-content-center"
                                style={{
                                    borderRadius: '12px',
                                    padding: 'clamp(8px,2vw,10px) clamp(12px,2.5vw,20px)',
                                    fontSize: 'clamp(14px,3vw,16px)',
                                    height: 'clamp(40px,8vw,44px)',
                                    minWidth: 'clamp(40px,10vw,120px)',
                                    flexShrink: 0,
                                    borderWidth: '2px',
                                    transition: 'all 0.3s ease',
                                    alignSelf: 'flex-start',
                                    color: downloadingFull ? '#9ca3af' : '#1e40af',
                                    backgroundColor: downloadingFull ? '#f3f4f6' : 'transparent',
                                    cursor: downloadingFull ? 'not-allowed' : 'pointer',
                                    whiteSpace: 'nowrap',
                                    gap: '7px',
                                    fontWeight: '600',
                                    border: '2px solid #1e40af',
                                }}
                                onMouseEnter={(e) => {
                                    if (!downloadingFull) {
                                        e.currentTarget.style.backgroundColor = '#1e40af';
                                        e.currentTarget.style.color = 'white';
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!downloadingFull) {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                        e.currentTarget.style.color = '#1e40af';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                    }
                                }}
                            >
                                {downloadingFull ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" style={{ width: '15px', height: '15px' }} />
                                        <span className="d-none d-sm-inline">Generating...</span>
                                        <span className="d-inline d-sm-none">Wait...</span>
                                    </>
                                ) : (
                                    <>
                                        <FileText size={16} className="flex-shrink-0" />
                                        <span className="d-none d-xl-inline">Download full Report</span>
                                        <span className="d-none d-lg-inline d-xl-none">Download</span>
                                        <span className="d-none d-md-inline d-lg-none">PDF</span>
                                    </>
                                )}
                            </button>
                        </div>

                        <div className="text-center w-100">
                            <h1 className="fw-bold mb-1" style={{
                                background: 'linear-gradient(90deg, #6366f1, #06b6d4)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                fontSize: 'clamp(1.1rem, 4vw, 1.9rem)',
                                lineHeight: '1.2',
                                wordBreak: 'break-word',
                                padding: '0 10px',
                            }}>
                                {pageTitle}
                            </h1>
                            <p className="text-muted m-0" style={{ fontSize: 'clamp(0.75rem, 2.5vw, 0.95rem)' }}>
                                Full Analysis • <strong style={{ color: '#0d53bd' }}>"{businessName}"</strong>
                            </p>
                            <p className="text-muted mt-1" style={{ fontSize: '12px' }}>
                                <Calendar size={14} className="me-1" />
                                {new Date(analysis.created_at).toLocaleDateString('en-IN', {
                                    day: 'numeric',
                                    month: 'long',
                                    year: 'numeric'
                                })}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="row g-3">
                    {currentSections.map((section, idx) => (
                        analysisData[section.key] && (
                            <div key={idx} className="col-12 col-sm-6 col-lg-4">
                                <SectionCard section={section} />
                            </div>
                        )
                    ))}
                </div>
            </div>
        </div>
    );
}

export default SavedAnalysisView;